<template>
  <section class="psa-workflow-rules-page tw-page">
    <div class="page-header">
      <h1 class="page-title">PSA Workflow Rules</h1>
      <p class="page-subtitle">Configure PSA ticket lifecycle and attribution rules for this company.</p>
    </div>

    <div v-if="isLoading" class="loading-state">
      <b-loading :is-full-page="false" :active="true" />
    </div>

    <div v-else-if="loadError" class="error-msg">
      {{ loadError }}
    </div>

    <div v-else-if="!editingRule" class="empty-state">
      <p>No PSA workflow rules configured yet.</p>
      <b-button type="is-primary" @click="startCreate">
        Configure Rules
      </b-button>
    </div>

    <div v-else>
      <!-- Form -->
      <div class="rule-form">
        <div class="rule-form__header">
          <h2>{{ editingRule._id ? 'PSA Workflow Rules' : 'New Rule Set' }}</h2>
          <div class="rule-form__header-actions">
            <b-button v-if="!editingRule._id" type="is-light" @click="cancelCreate">
              Cancel
            </b-button>
            <b-button v-if="editingRule._id" type="is-danger is-light" @click="confirmDelete">
              Delete
            </b-button>
          </div>
        </div>

        <!-- PSA System -->
        <div class="form-field">
          <label class="form-label">PSA System</label>
          <b-select v-model="editingRule.psaSystem">
            <option value="connectwise">ConnectWise</option>
          </b-select>
        </div>

        <!-- Ticket Lifecycle -->
        <div class="form-field">
          <label class="form-label">Ticket Lifecycle Status Meanings</label>
          <div
            v-for="status in lifecycleStatuses"
            :key="status.key"
            class="lifecycle-row"
          >
            <span class="lifecycle-key">{{ status.label }}</span>
            <b-input
              v-model="editingRule.ticketLifecycle[status.key]"
              :placeholder="`Description of '${status.key}' status`"
              class="lifecycle-input"
            />
          </div>
        </div>

        <!-- Attribution Priority -->
        <div class="form-field">
          <label class="form-label">Attribution Priority (ordered)</label>
          <div class="attribution-list">
            <div
              v-for="(item, idx) in editingRule.attributionPriority"
              :key="idx"
              class="attribution-item"
            >
              <span class="attribution-index">{{ idx + 1 }}.</span>
              <span class="attribution-value">{{ item }}</span>
            </div>
          </div>
          <p class="form-hint">Priority order is fixed for MVP. Edit via API for custom order.</p>
        </div>

        <!-- Closer Fallback Caution -->
        <div class="form-field">
          <label class="form-label">Closer Fallback Caution</label>
          <b-checkbox v-model="editingRule.closerFallbackCaution">
            Treat closer fallback attribution with caution (append attribution note to reports)
          </b-checkbox>
        </div>

        <!-- Filter Rules -->
        <div class="form-field">
          <label class="form-label">Ticket Filter Rules</label>
          <b-checkbox v-model="editingRule.filterRules.excludeNoTechnician" class="filter-check">
            Exclude tickets with no technician assigned
          </b-checkbox>
          <b-checkbox v-model="editingRule.filterRules.excludeZeroActualHours" class="filter-check">
            Exclude tickets where actual hours = 0
          </b-checkbox>
        </div>

        <!-- Exclude Boards -->
        <div class="form-field">
          <label class="form-label">Exclude Boards (exclude from analysis)</label>
          <b-input
            v-model="editingRule._excludeBoardsText"
            placeholder="e.g. LabTech, Workflow"
          />
          <p class="form-hint">Comma-separated list of board names whose tickets should be excluded from all analysis. Use this for monitoring, automation, or internal boards that are not meaningful service work.</p>
        </div>

        <!-- Exclude Clients -->
        <div class="form-field">
          <label class="form-label">Exclude Clients (exclude from analysis)</label>
          <b-input
            v-model="editingRule._excludeClientsText"
            placeholder="e.g. Internal Company, LLC"
          />
          <p class="form-hint">Comma-separated list of client/company names whose tickets should be excluded from all analysis. Use this for internal clients that are not meaningful service work — this is different from Exclude Boards, which filters by ConnectWise board name.</p>
        </div>

        <!-- Client-owned Queues -->
        <div class="form-field">
          <label class="form-label">Client-owned Queues (left out of daily and weekly reports)</label>
          <b-input
            v-model="editingRule._clientOwnedBoardsText"
            placeholder="e.g. Client IT Support, Co-Managed Queue"
          />
          <p class="form-hint">Comma-separated list of board names the client's own IT team works (co-managed queues). Their tickets are left out of the daily and weekly reports entirely. Board names must match exactly (case is ignored).</p>
        </div>

        <!-- Alert Boards -->
        <div class="form-field">
          <label class="form-label">Alert Boards (no follow-ups unless urgent and unhandled)</label>
          <b-input
            v-model="editingRule._alertBoardsText"
            placeholder="e.g. Monitoring Alerts"
          />
          <b-checkbox v-model="editingRule._alertBoardsFallback" class="filter-check">
            When this list is empty, use the Exclude Boards that aren't client-owned queues
          </b-checkbox>
          <p class="form-hint">Comma-separated list of monitoring/alert board names. Daily and weekly report follow-ups that only cite tickets on these boards are dropped, unless a ticket is High/Critical/Emergency, still open and nobody has worked it. Board names must match exactly (case is ignored). With the list empty and the box unticked, no board is treated as an alert board.</p>
        </div>

        <!-- Automation Accounts -->
        <div class="form-field">
          <label class="form-label">Automation / Bot Accounts (exclude from analysis)</label>
          <b-input
            v-model="editingRule._automationAccountsText"
            placeholder="e.g. AutoBot, MonitoringAgent"
          />
          <p class="form-hint">Comma-separated list of technician names that represent automated or unattended accounts.</p>
        </div>

        <!-- Valid Email Domains -->
        <div class="form-field">
          <label class="form-label">Valid Email Domains</label>
          <b-input
            v-model="editingRule._validEmailDomainsText"
            placeholder="e.g. company.com, company.io"
          />
          <p class="form-hint">Comma-separated. All listed domains are treated as belonging to the same org.</p>
        </div>

        <!-- Invalid Email Domains -->
        <div class="form-field">
          <label class="form-label">Invalid / Typo Email Domains (ignore)</label>
          <b-input
            v-model="editingRule._invalidEmailDomainsText"
            placeholder="e.g. companytypo.com"
          />
          <p class="form-hint">Comma-separated. Any records under these domains will be excluded.</p>
        </div>

        <!-- Role Rules -->
        <div class="form-field">
          <label class="form-label">Role Rules</label>
          <div
            v-for="(roleRule, idx) in editingRule.roleRules"
            :key="idx"
            class="role-rule-card"
          >
            <div class="role-rule-row-1">
              <span class="role-rule-number">{{ idx + 1 }}</span>
              <b-input
                v-model="roleRule.identifier"
                placeholder="Name"
                class="role-rule-identifier"
              />
              <b-input
                v-model="roleRule._aliasesText"
                placeholder="Aliases (comma-sep)"
                class="role-rule-aliases"
              />
              <b-select v-model="roleRule.roleType" class="role-rule-type">
                <option value="dispatcher">Dispatcher</option>
                <option value="qa_admin_closer">QA / Admin Closer</option>
                <option value="admin">Admin</option>
              </b-select>
              <b-button size="is-small" type="is-danger is-light" @click="removeRoleRule(idx)">
                Remove
              </b-button>
            </div>
            <div class="role-rule-row-2">
              <b-checkbox v-model="roleRule.ignoreForTechnicalAttribution">
                Ignore for tech attribution
              </b-checkbox>
              <b-checkbox v-model="roleRule.treatClosureAsAdmin">
                Treat closure as admin
              </b-checkbox>
            </div>
            <div class="role-rule-row-3">
              <b-input
                v-model="roleRule.notes"
                type="textarea"
                placeholder="Notes (optional)"
                rows="2"
                class="role-rule-notes-area"
              />
            </div>
          </div>
          <b-button size="is-small" type="is-light" @click="addRoleRule" class="add-role-btn">
            + Add Role Rule
          </b-button>
        </div>

        <!-- Save / Error -->
        <div v-if="saveError" class="error-msg">{{ saveError }}</div>
        <div v-if="saveSuccess" class="success-msg">Rule set saved successfully.</div>

        <div class="form-actions">
          <b-button type="is-primary" :loading="isSaving" @click="saveRule">
            Save
          </b-button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <b-modal
      v-model="showDeleteModal"
      :can-cancel="['escape', 'outside', 'button']"
      :width="420"
      trap-focus
    >
      <div class="confirm-modal">
        <h2 class="confirm-modal__title">Delete Rule Set</h2>
        <p class="confirm-modal__body">
          Are you sure you want to delete the PSA workflow rules for this company? This cannot be undone.
        </p>
        <div class="confirm-modal__actions">
          <b-button type="is-light" @click="showDeleteModal = false">Cancel</b-button>
          <b-button type="is-danger" :loading="isDeleting" @click="executeDelete">Delete</b-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { useUserStore } from '@/stores/userStore';

const BASE_URL = process.env.VUE_APP_API_URL;

const LIFECYCLE_STATUSES = [
  { key: 'completed', label: 'Completed' },
  { key: 'qa', label: 'QA' },
  { key: 'closed', label: 'Closed' },
];

const DEFAULT_ATTRIBUTION_PRIORITY = [
  'timeEntry',
  'resolvedBy',
  'assignedResource',
  'owner',
  'closedBy',
];

export default {
  name: 'PsaWorkflowRules',

  data() {
    return {
      savedRule: null,
      editingRule: null,
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      showDeleteModal: false,
      saveSuccess: false,
      saveError: null,
      loadError: null,
      lifecycleStatuses: LIFECYCLE_STATUSES,
    };
  },

  created() {
    this.userStore = useUserStore();
  },

  mounted() {
    this.loadRule();
  },

  methods: {
    authConfig() {
      return { headers: { Authorization: this.$cookies.get('teamwrkr-token') } };
    },

    async loadRule() {
      this.isLoading = true;
      this.loadError = null;
      try {
        const response = await this.axios.get(
          `${BASE_URL}/companies/context/psa-workflow-rules`,
          this.authConfig()
        );
        const existing = (response.data.rules || [])[0] || null;
        this.savedRule = existing;
        this.editingRule = existing ? this.toFormState(existing) : null;
      } catch (err) {
        this.loadError = err?.response?.data?.message || 'Could not load PSA workflow rules.';
      } finally {
        this.isLoading = false;
      }
    },

    toFormState(rule) {
      const filterRules = rule.filterRules || {};
      return {
        ...rule,
        ticketLifecycle: { ...(rule.ticketLifecycle || {}) },
        attributionPriority: Array.isArray(rule.attributionPriority)
          ? [...rule.attributionPriority]
          : [...DEFAULT_ATTRIBUTION_PRIORITY],
        roleRules: Array.isArray(rule.roleRules)
          ? rule.roleRules.map(r => ({
              ...r,
              _aliasesText: Array.isArray(r.aliases) ? r.aliases.join(', ') : '',
            }))
          : [],
        // KAN-99: every filterRules key is kept (mergedBoards, clientOwnedBoards, keys this form
        // doesn't show) — the update API replaces the whole filterRules object, so dropping a
        // key here wiped it from the saved rules.
        filterRules: {
          ...filterRules,
          excludeNoTechnician: !!filterRules.excludeNoTechnician,
          excludeZeroActualHours: !!filterRules.excludeZeroActualHours,
          excludeBoards: Array.isArray(filterRules.excludeBoards) ? [...filterRules.excludeBoards] : [],
          excludeClients: Array.isArray(filterRules.excludeClients) ? [...filterRules.excludeClients] : [],
        },
        _excludeBoardsText: Array.isArray(filterRules.excludeBoards)
          ? filterRules.excludeBoards.join(', ')
          : '',
        _excludeClientsText: Array.isArray(filterRules.excludeClients)
          ? filterRules.excludeClients.join(', ')
          : '',
        _clientOwnedBoardsText: Array.isArray(filterRules.clientOwnedBoards)
          ? filterRules.clientOwnedBoards.join(', ')
          : '',
        // Absent (not empty) alertBoards means "fall back to excludeBoards" on the backend, so
        // the field stays absent unless the admin types something.
        _alertBoardsText: Array.isArray(filterRules.alertBoards)
          ? filterRules.alertBoards.join(', ')
          : '',
        _alertBoardsFallback: !Array.isArray(filterRules.alertBoards),
        _automationAccountsText: Array.isArray(rule.automationAccounts)
          ? rule.automationAccounts.join(', ')
          : '',
        _validEmailDomainsText: Array.isArray(rule.validEmailDomains)
          ? rule.validEmailDomains.join(', ')
          : '',
        _invalidEmailDomainsText: Array.isArray(rule.invalidEmailDomains)
          ? rule.invalidEmailDomains.join(', ')
          : '',
      };
    },

    startCreate() {
      this.saveSuccess = false;
      this.saveError = null;
      this.editingRule = {
        psaSystem: 'connectwise',
        ticketLifecycle: {},
        attributionPriority: [...DEFAULT_ATTRIBUTION_PRIORITY],
        roleRules: [],
        closerFallbackCaution: true,
        filterRules: { excludeNoTechnician: false, excludeZeroActualHours: false, excludeBoards: [], excludeClients: [] },
        _excludeBoardsText: '',
        _excludeClientsText: '',
        _clientOwnedBoardsText: '',
        _alertBoardsText: '',
        _alertBoardsFallback: true,
        _automationAccountsText: '',
        _validEmailDomainsText: '',
        _invalidEmailDomainsText: '',
      };
    },

    cancelCreate() {
      this.editingRule = null;
      this.saveSuccess = false;
      this.saveError = null;
    },

    addRoleRule() {
      this.editingRule.roleRules.push({
        identifier: '',
        _aliasesText: '',
        roleType: 'dispatcher',
        ignoreForTechnicalAttribution: false,
        treatClosureAsAdmin: false,
        notes: '',
      });
    },

    removeRoleRule(idx) {
      this.editingRule.roleRules.splice(idx, 1);
    },

    buildPayload() {
      const splitTrimmed = text =>
        (text || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);

      const roleRules = (this.editingRule.roleRules || []).map(r => {
        const { _aliasesText, ...rest } = r;
        return { ...rest, aliases: splitTrimmed(_aliasesText) };
      });

      const {
        _excludeBoardsText,
        _excludeClientsText,
        _clientOwnedBoardsText,
        _alertBoardsText,
        _alertBoardsFallback,
        _automationAccountsText,
        _validEmailDomainsText,
        _invalidEmailDomainsText,
        ...base
      } = this.editingRule;

      // KAN-99: an empty Alert Boards field with the fallback box ticked leaves alertBoards absent,
      // which the backend reads as "use the Exclude Boards that aren't client-owned queues"; unticked,
      // it saves an explicit empty list (no alert boards).
      const otherFilterRules = { ...(base.filterRules || {}) };
      delete otherFilterRules.alertBoards;
      const alertBoards = splitTrimmed(_alertBoardsText);
      const keepAlertBoards = alertBoards.length > 0 || !_alertBoardsFallback;

      return {
        ...base,
        roleRules,
        filterRules: {
          ...otherFilterRules,
          excludeBoards: splitTrimmed(_excludeBoardsText),
          excludeClients: splitTrimmed(_excludeClientsText),
          clientOwnedBoards: splitTrimmed(_clientOwnedBoardsText),
          ...(keepAlertBoards ? { alertBoards } : {}),
        },
        automationAccounts: splitTrimmed(_automationAccountsText),
        validEmailDomains: splitTrimmed(_validEmailDomainsText),
        invalidEmailDomains: splitTrimmed(_invalidEmailDomainsText),
      };
    },

    async saveRule() {
      this.isSaving = true;
      this.saveSuccess = false;
      this.saveError = null;
      try {
        const payload = this.buildPayload();
        // savedRule (not a field on the payload) is the source of truth for whether a
        // rule already exists — the Prisma-backed API returns companyId, not the old
        // Mongoose `company` field, so checking payload.company here always failed
        // shut to POST, and re-saving an existing rule hit the unique constraint on
        // (company_id, psa_system).
        if (this.savedRule) {
          await this.axios.put(
            `${BASE_URL}/companies/context/psa-workflow-rules/${this.savedRule.companyId}`,
            payload,
            this.authConfig()
          );
        } else {
          await this.axios.post(
            `${BASE_URL}/companies/context/psa-workflow-rules`,
            payload,
            this.authConfig()
          );
        }
        this.saveSuccess = true;
        await this.loadRule();
      } catch (err) {
        this.saveError = err?.response?.data?.message || 'Could not save rule set. Please try again.';
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteModal = true;
    },

    async executeDelete() {
      this.isDeleting = true;
      try {
        await this.axios.delete(
          `${BASE_URL}/companies/context/psa-workflow-rules/${this.savedRule?.companyId}`,
          this.authConfig()
        );
        this.savedRule = null;
        this.editingRule = null;
        this.showDeleteModal = false;
      } catch (err) {
        this.loadError = err?.response?.data?.message || 'Could not delete rule set.';
        this.showDeleteModal = false;
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables';

.psa-workflow-rules-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  color: #666;
  font-size: 0.9rem;
}

.rule-form {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1.5rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;

    h2 {
      font-size: 1.125rem;
      font-weight: 600;
    }
  }

  &__header-actions {
    display: flex;
    gap: 0.5rem;
  }
}

.form-field {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.4rem;
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.75rem;
  color: #888;
  margin-top: 0.25rem;
}

.lifecycle-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.lifecycle-key {
  width: 90px;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: capitalize;
  flex-shrink: 0;
}

.lifecycle-input {
  flex: 1;
}

.attribution-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.attribution-item {
  background: #f0f0f0;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  display: flex;
  gap: 0.25rem;
}

.attribution-index {
  color: #888;
}

.role-rule-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.filter-check {
  display: block;
  margin-bottom: 0.4rem;
}

.role-rule-card {
  border: 1px solid #e4e4e4;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: #fafafa;
}

.role-rule-row-1 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.role-rule-number {
  font-weight: 600;
  font-size: 0.875rem;
  color: #999;
  min-width: 1.25rem;
  flex-shrink: 0;
}

.role-rule-identifier {
  width: 130px;
  flex-shrink: 0;
}

.role-rule-aliases {
  width: 180px;
  flex-shrink: 0;
}

.role-rule-type {
  width: 150px;
  flex-shrink: 0;
}

.role-rule-row-2 {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
  padding-left: 1.75rem;
  font-size: 0.875rem;
}

.role-rule-row-3 {
  padding-left: 1.75rem;
}

.role-rule-notes-area {
  textarea {
    resize: vertical;
    min-height: 3rem;
  }
}

.add-role-btn {
  margin-top: 0.4rem;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.error-msg {
  color: #c0392b;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.success-msg {
  color: #27ae60;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #666;

  p {
    margin-bottom: 1rem;
  }
}

.loading-state {
  min-height: 120px;
  position: relative;
}

.confirm-modal {
  background: #fff;
  border-radius: 8px;
  padding: 1.75rem;

  &__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
  }

  &__body {
    font-size: 0.9rem;
    color: #555;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}
</style>
